import React, { useState } from 'react';
import friendlyError from '../../utils/friendlyError';
import styled from 'styled-components';
import {
  FaSave, FaSyncAlt, FaCheckCircle, FaExclamationTriangle, FaKey, FaEnvelope,
} from 'react-icons/fa';
import {
  TechSection, TechButton, TechButtonGhost, TechInput, StatusPill,
  mono, borderSoft, panelDeep,
} from '../../styles/techComponents';
import { updateProfileCredentials } from '../../services/digitalProfilesService';

const Table = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const Row = styled.div`
  display: grid;
  grid-template-columns: 140px 1fr 1fr 1fr 80px;
  gap: 0.4rem;
  align-items: center;
  padding: 0.5rem 0.6rem;
  background: ${p => p.$incomplete ? 'rgba(255, 84, 112, 0.06)' : panelDeep};
  border: 1px solid ${p => p.$incomplete ? 'rgba(255, 84, 112, 0.3)' : borderSoft};
  border-radius: 6px;
  
  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const HeaderRow = styled.div`
  display: grid;
  grid-template-columns: 140px 1fr 1fr 1fr 80px;
  gap: 0.4rem;
  padding: 0.3rem 0.6rem;
  font-family: ${mono};
  font-size: 0.65rem;
  text-transform: uppercase;
  letter-spacing: 0.12em;
  color: var(--text-disabled);
  
  @media (max-width: 900px) {
    display: none;
  }
`;

const AccountLabel = styled.div`
  font-size: 0.85rem;
  font-weight: 600;
  color: var(--text-primary);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  
  .platform {
    font-size: 0.65rem;
    color: var(--text-disabled);
    text-transform: uppercase;
    font-family: ${mono};
    display: block;
  }
`;

const SmallInput = styled(TechInput)`
  padding: 0.35rem 0.5rem;
  font-size: 0.82rem;
  height: 32px;
`;

const StatusIcon = styled.span`
  font-size: 0.75rem;
  ${p => p.$ok ? 'color: var(--alert-success, #38d39f)' : 'color: var(--text-disabled)'}
`;

const SaveBtn = styled.button`
  padding: 0.3rem 0.5rem;
  background: ${p => p.$saved ? 'rgba(56, 211, 159, 0.15)' : 'rgba(0, 180, 216, 0.12)'};
  border: 1px solid ${p => p.$saved ? 'var(--alert-success)' : 'var(--accent)'};
  color: ${p => p.$saved ? 'var(--alert-success)' : 'var(--accent)'};
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.75rem;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.3rem;
  transition: all 0.15s;
  
  &:disabled { opacity: 0.5; cursor: not-allowed; }
  &:hover:not(:disabled) { background: rgba(0, 180, 216, 0.25); }
`;

const Legend = styled.div`
  display: flex;
  gap: 1rem;
  font-size: 0.75rem;
  color: var(--text-secondary);
  flex-wrap: wrap;
  margin-bottom: 0.5rem;
`;

const EditorCredencialesMasivo = ({ profiles, onSaved }) => {
  const [rows, setRows] = useState(() =>
    profiles.map(p => ({
      id: p.id,
      platform: p.platform,
      username: p.username,
      display_name: p.display_name || '',
      login_user: p.login_user || '',
      password: '',          // nunca mostramos el guardado
      email_address: p.email_address || '',
      saving: false,
      saved: false,
      error: null,
      // Para saber si ya tiene datos
      has_password: p.has_password,
      has_sessionid: p.has_sessionid,
    }))
  );

  const updateRow = (id, field, value) => {
    setRows(prev => prev.map(r => r.id === id ? { ...r, [field]: value, saved: false } : r));
  };

  const saveRow = async (id) => {
    const row = rows.find(r => r.id === id);
    if (!row) return;

    setRows(prev => prev.map(r => r.id === id ? { ...r, saving: true, error: null } : r));

    try {
      const payload = {};
      if (row.login_user.trim()) payload.login_user = row.login_user.trim();
      if (row.password.trim()) payload.password = row.password.trim();
      if (row.email_address.trim()) payload.email_address = row.email_address.trim();

      if (Object.keys(payload).length === 0) {
        setRows(prev => prev.map(r => r.id === id ? { ...r, saving: false, error: 'Sin cambios' } : r));
        return;
      }

      await updateProfileCredentials(id, payload);

      setRows(prev => prev.map(r => r.id === id ? {
        ...r,
        saving: false,
        saved: true,
        has_password: row.password.trim() ? true : r.has_password,
        password: '',  // Limpiar después de guardar
        error: null,
      } : r));

      onSaved?.();
    } catch (err) {
      setRows(prev => prev.map(r => r.id === id ? {
        ...r,
        saving: false,
        error: friendlyError(err),
      } : r));
    }
  };

  const saveAll = async () => {
    const pendingRows = rows.filter(r =>
      r.login_user.trim() || r.password.trim() || r.email_address.trim()
    );
    for (const row of pendingRows) {
      await saveRow(row.id);
    }
  };

  const incomplete = rows.filter(r => !r.has_password || !r.login_user);
  const complete = rows.filter(r => r.has_password && r.login_user);

  return (
    <TechSection
      title="🔑 Credenciales de Usuarios"
      action={
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
          <StatusPill>
            <FaCheckCircle style={{ color: 'var(--alert-success)' }} /> {complete.length} completos
          </StatusPill>
          {incomplete.length > 0 && (
            <StatusPill style={{ color: 'var(--alert-error)' }}>
              <FaExclamationTriangle /> {incomplete.length} incompletos
            </StatusPill>
          )}
          <TechButton type="button" onClick={saveAll} style={{ padding: '0.4rem 0.8rem', fontSize: '0.8rem' }}>
            <FaSave /> Guardar todos
          </TechButton>
        </div>
      }
      style={{ marginBottom: '1.25rem' }}
    >
      <Legend>
        <span><FaKey style={{ color: '#38d39f' }} /> = tiene password</span>
        <span><FaEnvelope style={{ color: '#38d39f' }} /> = tiene email</span>
        <span style={{ color: 'var(--alert-error)' }}>Fondo rojo = faltan datos</span>
        <span>Deja vacío el campo password para no sobreescribirlo</span>
      </Legend>

      <HeaderRow>
        <span>Cuenta</span>
        <span>Login User / Handle</span>
        <span>Password</span>
        <span>Email registrado</span>
        <span>Guardar</span>
      </HeaderRow>

      <Table>
        {rows.map(row => (
          <Row key={row.id} $incomplete={!row.has_password || !row.login_user}>
            <AccountLabel>
              @{row.username}
              <span className="platform">{row.platform}</span>
            </AccountLabel>

            <div style={{ display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
              <SmallInput
                placeholder="@usuario o email de login"
                value={row.login_user}
                onChange={e => updateRow(row.id, 'login_user', e.target.value)}
              />
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
              <StatusIcon $ok={row.has_password}><FaKey /></StatusIcon>
              <SmallInput
                type="password"
                placeholder={row.has_password ? '••••••• (dejar vacío = no cambiar)' : 'Nueva contraseña'}
                value={row.password}
                onChange={e => updateRow(row.id, 'password', e.target.value)}
              />
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
              <StatusIcon $ok={!!row.email_address}><FaEnvelope /></StatusIcon>
              <SmallInput
                placeholder="email@dominio.com"
                value={row.email_address}
                onChange={e => updateRow(row.id, 'email_address', e.target.value)}
              />
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
              <SaveBtn
                onClick={() => saveRow(row.id)}
                disabled={row.saving}
                $saved={row.saved}
              >
                {row.saving ? <FaSyncAlt /> : row.saved ? <FaCheckCircle /> : <FaSave />}
                {row.saving ? '' : row.saved ? 'OK' : 'Save'}
              </SaveBtn>
              {row.error && (
                <span style={{ fontSize: '0.6rem', color: 'var(--alert-error)', lineHeight: 1.2 }}>
                  {row.error}
                </span>
              )}
            </div>
          </Row>
        ))}
      </Table>

      {rows.length === 0 && (
        <div style={{ textAlign: 'center', padding: '1.5rem', color: 'var(--text-secondary)', fontStyle: 'italic' }}>
          No hay perfiles cargados
        </div>
      )}
    </TechSection>
  );
};

export default EditorCredencialesMasivo;
